<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import StoryPickerModal from '@/components/StoryPickerModal.vue'
import { formatStoryMapId, formatStoryMapSelectLabel } from '@/features/story/storyTreeProjection'
import { useSceneEditorStore } from '@/store/sceneEditor'

const props = withDefaults(defineProps<{
  modelValue: number
  placeholder?: string
  disabled?: boolean
  readonly?: boolean
  pickerTitle?: string
}>(), {
  readonly: false,
})

const emit = defineEmits<{
  'update:modelValue': [value: number]
}>()

const editor = useSceneEditorStore()
const pickerOpen = ref(false)
const localValue = ref(props.modelValue)

watch(
  () => props.modelValue,
  (value) => {
    localValue.value = value
  },
)

const displayValue = computed(() => {
  const current = editor.storyTreeList.find((item) => Number(item.Id ?? 0) === Number(localValue.value))
  if (current) return formatStoryMapSelectLabel(current)
  if (Number.isFinite(Number(localValue.value)) && Number(localValue.value) > 0) {
    return `当前值：${formatStoryMapId(Number(localValue.value))}`
  }
  return props.placeholder ?? '请选择剧情'
})

function openPicker() {
  if (props.disabled || props.readonly) return
  pickerOpen.value = true
}

function updateValue(value: number) {
  localValue.value = value
  emit('update:modelValue', value)
}
</script>

<template>
  <div class="story-select-field">
    <input
      class="story-select-input"
      type="text"
      :value="displayValue"
      :disabled="disabled"
      :readonly="true"
      :title="displayValue"
      @click="openPicker"
    />
    <button
      type="button"
      class="story-select-trigger"
      :disabled="disabled || readonly"
      :title="pickerTitle ?? '选择剧情'"
      @click="openPicker"
    >
      <svg viewBox="0 0 16 16" aria-hidden="true">
        <path
          d="M11.6 1.4a1.5 1.5 0 0 1 2.1 2.1l-7.3 7.3-3 0.8 0.8-3 7.4-7.2Zm1.4 2.1a.5.5 0 0 0-.7-.7L5.2 9.9l-0.4 1.4 1.4-0.4L13 3.5ZM2 13h12v1H2z"
          fill="currentColor"
        />
      </svg>
    </button>

    <StoryPickerModal
      v-if="pickerOpen"
      v-model:show="pickerOpen"
      :title="pickerTitle ?? '选择剧情'"
      :selected-id="localValue"
      @select="updateValue"
    />
  </div>
</template>

<style scoped>
.story-select-field {
  position: relative;
  display: block;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  flex: 1 1 0;
  box-sizing: border-box;
}

.story-select-input {
  width: 100%;
  min-width: 0;
  height: 28px;
  padding: 4px 34px 4px 8px;
  border: 1px solid var(--theme-border);
  background: var(--theme-bg-card);
  color: var(--theme-text-heading);
  font-size: 12px;
  line-height: 20px;
  box-sizing: border-box;
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.story-select-input:focus {
  outline: none;
  border-color: var(--theme-accent);
}

.story-select-trigger {
  position: absolute;
  top: 1px;
  right: 1px;
  width: 26px;
  height: calc(100% - 2px);
  padding: 0;
  border: 0;
  border-left: 1px solid var(--theme-border);
  background: var(--theme-bg-surface);
  color: var(--theme-text-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.story-select-trigger:hover:not(:disabled) {
  color: var(--theme-text-heading);
  background: var(--theme-bg-raised);
}

.story-select-trigger svg {
  width: 14px;
  height: 14px;
}
</style>
